import {
  FolderSelectionTree,
  LocationType,
} from '@/app/(dashboard)/projects/FolderSelectionTree'

import {
  Project,
  Button,
  BaseDialog,
  useToast,
  useQueryClient,
  ProjectNew,
  useMoveProject,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import {
  Folder,
  FoldersListItem,
} from '@/dc-shared-web/src/hooks/api/folder/types'
import { useMoveFolder } from '@/dc-shared-web/src/hooks/api/folder/useMoveFolder'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'

import { useState } from 'react'

export const MoveDialog = ({
  project,
  folder,
  teamUuid,
  onClose,
  onSuccess,
  type,
  open,
  initialLocation,
}: {
  project?: ProjectNew | Project
  folder?: FoldersListItem | Folder
  teamUuid: string
  onClose: () => void
  onSuccess: () => void
  type: 'project' | 'folder'
  open: boolean
  initialLocation: LocationType
}) => {
  const queryClient = useQueryClient()
  const { trackEvent } = useFSTrackEvent()

  const { toast } = useToast()
  const [selectedLocation, setSelectedLocation] = useState<LocationType>()

  const [isExecuting, setIsExecuting] = useState(false)

  const moveFolderMutation = useMoveFolder()
  const moveProjectMutation = useMoveProject()

  const onSuccessfulMove = async () => {
    const title = type === 'project' ? project?.name : folder?.name
    toast({
      variant: 'success',
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} moved`,
      description: `${title} moved to ${selectedLocation?.name}.`,
    })

    setIsExecuting(true)

    await Promise.allSettled([
      queryClient.invalidateQueries({
        queryKey: ['projects', 'list'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['project'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['folders', 'list'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['folder', folder?.uuid],
      }),
      queryClient.invalidateQueries({
        queryKey: ['FolderForMove'],
      }),
    ])

    setIsExecuting(false)

    onSuccess()
  }

  const onMove = () => {
    setIsExecuting(true)

    if (type === 'folder') {
      trackEvent(FS.FOLDER_MOVED, {
        folder_id: folder?.uuid || '',
        folder_name: folder?.name || '',
        team: teamUuid,
      })
      moveFolderMutation.mutate({
        name: folder?.name || '',
        parent_uuid: selectedLocation?.id?.toString() || '',
        type: selectedLocation?.type || 'dashboard',
        uuid: folder?.uuid || '',
        office_uuid: teamUuid,
      })
      onSuccessfulMove()
      return
    }
    if (type === 'project' && project?.uuid) {
      trackEvent(FS.PROJECT_MOVED, {
        project_id: project?.uuid || '',
        project_name: project?.name || '',
        team: teamUuid,
      })
      moveProjectMutation.mutate({
        parent_uuid: selectedLocation?.id?.toString() || '',
        office_uuid: teamUuid,
        uuid: project?.uuid,
      })
      onSuccessfulMove()
    }
  }

  const onDialogClose = () => {
    /* We avoid closing the dialog if an update is still in progress */
    if (isExecuting) return

    onClose()
  }

  return (
    <BaseDialog
      title='Move'
      isOpen={open}
      onClose={onDialogClose}
      size='large'
      primaryActionSlot={
        <Button
          onClick={onMove}
          disabled={
            /*
              We disallow moving the project if:
              - there is no selected location
              - the selected location is the same as the initial location
              - the move is still in progress
            */
            !selectedLocation ||
            selectedLocation?.id === initialLocation?.id ||
            isExecuting
          }
          data-testid='move-project-confirm-btn'
        >
          {isExecuting ? 'Moving...' : 'Move'}
        </Button>
      }
    >
      <div className='tw-font-normal' data-testid='move-project-dialog'>
        <FolderSelectionTree
          name={project?.name || folder?.name || ''}
          type={type}
          teamUuid={teamUuid}
          initialLocation={initialLocation}
          selectedLocation={selectedLocation}
          onLocationChange={setSelectedLocation}
          itemToMove={type === 'project' ? project : folder}
        />
      </div>
    </BaseDialog>
  )
}
